import React from 'react';

import { MdChevronRight, MdChevronLeft } from 'react-icons/md';
import {
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Icon,
  Stack,
  Select,
} from '@chakra-ui/react';
import { flexRender } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

interface Props {
  table: any;
  limit: number;
  pageIndex: number;
  pages: number[];
  dir: string;
  handleChangePageIndex: (pageNumber: number) => void;
  handleChangeRowSize: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

const DEFAULT_PAGE_SIZES: number[] = [6, 7, 8, 9, 10];

export default function SuppliersTable(props: Props) {
  const {
    table,
    limit,
    pageIndex,
    pages,
    dir,
    handleChangePageIndex,
    handleChangeRowSize,
  } = props;

  const { t } = useTranslation();

  // colors
  const textColor = useColorModeValue('navy.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const btnBorderColor = useColorModeValue('gray.200', 'white');
  const brandColor = useColorModeValue('brand.500', 'brand.400');

  return (
    <>
      <Table variant="simple" color="gray.500" mb="24px">
        <Thead>
          {table.getHeaderGroups().map((headerGroup: any) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header: any) => {
                return (
                  <Th
                    pe="10px"
                    borderColor={borderColor}
                    key={header.id}
                    colSpan={header.colSpan}
                  >
                    {header.isPlaceholder ? null : (
                      <Flex
                        {...{
                          className: header.column.getCanSort()
                            ? 'cursor-pointer select-none'
                            : '',
                          onClick: header.column.getToggleSortingHandler(),
                        }}
                        justify="space-between"
                        align="center"
                        fontSize={{ sm: '10px', lg: '12px' }}
                        color="gray.400"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {{
                          asc: '',
                          desc: '',
                        }[header.column.getIsSorted() as string] ?? null}
                      </Flex>
                    )}
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.map((row: any) => {
            return (
              <Tr px="20px" key={row.id}>
                {row.getVisibleCells().map((cell: any) => {
                  return (
                    <Td
                      key={cell.id}
                      fontSize={{ sm: '14px' }}
                      minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                      borderColor={borderColor}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </Td>
                  );
                })}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
      <Flex w="100%" justify="space-between" px="20px" pt="10px" pb="5px">
        <Flex align="center">
          <Text
            me="10px"
            minW="max-content"
            fontSize="sm"
            color="gray.500"
            fontWeight="normal"
          >
            {t('user_table.rows_per_page')}
          </Text>
          <Select
            dir={dir}
            fontSize="sm"
            variant="main"
            value={limit}
            onChange={handleChangeRowSize}
          >
            {DEFAULT_PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </Select>
        </Flex>
        <div className="flex items-center gap-2">
          <Stack direction="row" alignSelf="flex-end" spacing="4px" ms="auto">
            <Button
              variant="no-effects"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              transition="all .5s ease"
              w="40px"
              h="40px"
              borderRadius="50%"
              bg="transparent"
              border="1px solid"
              borderColor={btnBorderColor}
              display={
                limit === 5
                  ? 'none'
                  : table.getCanPreviousPage()
                  ? 'flex'
                  : 'none'
              }
              _hover={{
                bg: 'whiteAlpha.100',
                opacity: '0.7',
              }}
            >
              <Icon as={MdChevronLeft} w="16px" h="16px" color={textColor} />
            </Button>
            {pages.map((pageNumber, index) => {
              return (
                <Button
                  variant="no-effects"
                  transition="all .5s ease"
                  onClick={() => handleChangePageIndex(pageNumber)}
                  w="40px"
                  h="40px"
                  borderRadius="50%"
                  bg={pageNumber === pageIndex ? brandColor : 'transparent'}
                  border={
                    pageNumber === pageIndex ? 'none' : '1px solid lightgray'
                  }
                  _hover={
                    pageNumber === pageIndex
                      ? {
                          opacity: '0.7',
                        }
                      : {
                          bg: 'whiteAlpha.100',
                        }
                  }
                  key={index}
                >
                  <Text
                    fontSize="sm"
                    color={pageNumber === pageIndex ? '#fff' : textColor}
                  >
                    {pageNumber}
                  </Text>
                </Button>
              );
            })}
            <Button
              variant="no-effects"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              transition="all .5s ease"
              w="40px"
              h="40px"
              borderRadius="50%"
              bg="transparent"
              border="1px solid"
              borderColor={btnBorderColor}
              display={
                limit === 5 ? 'none' : table.getCanNextPage() ? 'flex' : 'none'
              }
              _hover={{
                bg: 'whiteAlpha.100',
                opacity: '0.7',
              }}
            >
              <Icon as={MdChevronRight} w="16px" h="16px" color={textColor} />
            </Button>
          </Stack>
        </div>
      </Flex>
    </>
  );
}
